import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  ContractTerminationRequestModel,
  ContractTerminationListRequestModel,
  ContractTerminationListResponseModel,
  ContractTerminationModel,
  ContractTerminationAlertListRequestModel,
  ContractTerminationAlertListResponseModel,
} from '../../model';

@Injectable({
  providedIn: 'root',
})
export class ContractsTerminationService {
  constructor(private http: HttpClient) {}

  public initializeContractTermination(contractId: number): Observable<ContractTerminationRequestModel> {
    return this.http.get<ContractTerminationRequestModel>(
      `${environment.apiEndpoint}/b2b/contracts/termination/init/${contractId}`,
    );
  }

  public startContractTermination(
    body: ContractTerminationRequestModel,
    files?: File[],
  ): Observable<ContractTerminationModel> {
    const formData = new FormData();
    formData.append('payload', new Blob([JSON.stringify(body)], { type: 'application/json' }));

    if (files?.length) {
      files.forEach((file) => formData.append('files', file));
    }

    return this.http.post<ContractTerminationModel>(`${environment.apiEndpoint}/b2b/contracts/termination`, formData);
  }

  public getTerminationsByContractIdAndClientId(
    contractId: number,
    clientId: string,
  ): Observable<ContractTerminationModel> {
    return this.http.get<ContractTerminationModel>(`${environment.apiEndpoint}/b2b/contracts/termination`);
  }

  public getTerminationsByContractId(contractId: number): Observable<ContractTerminationModel[]> {
    return this.http.get<ContractTerminationModel[]>(
      `${environment.apiEndpoint}/b2b/contracts/termination/contract/${contractId}`,
    );
  }

  public getTerminationsByClientId(clientId: string): Observable<ContractTerminationModel> {
    return this.http.get<ContractTerminationModel>(
      `${environment.apiEndpoint}/b2b/contracts/termination/client/${clientId}`,
    );
  }

  public getTerminationList(
    request: ContractTerminationListRequestModel,
  ): Observable<ContractTerminationListResponseModel> {
    let params: HttpParams = new HttpParams();

    if (request?.page) {
      params = params.set('page', request.page);
    }

    if (request?.page_size) {
      params = params.set('page_size', request.page_size);
    }

    if (request?.sort_direction) {
      params = params.set('sort_direction', request.sort_direction);
    }

    if (request?.sort_by) {
      params = params.set('sort_by', request.sort_by);
    }

    if (request?.status) {
      params = params.set('status', request.status);
    }

    return this.http.get<ContractTerminationListResponseModel>(
      `${environment.apiEndpoint}/b2b/contracts/termination/advisor`,
      {
        params,
      },
    );
  }

  public getSingleTermination(terminationId: number, clientId: string): Observable<ContractTerminationModel> {
    return this.http.get<ContractTerminationModel>(
      `${environment.apiEndpoint}/b2b/contracts/termination/${terminationId}/client/${clientId}`,
    );
  }

  public resentExistTermination(terminationId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiEndpoint}/b2b/contracts/termination/${terminationId}/resend`, null);
  }

  public updateStatus(
    terminationId: number,
    body: { status: 'OPEN' | 'CLOSED' },
  ): Observable<ContractTerminationModel> {
    return this.http.patch<ContractTerminationModel>(
      `${environment.apiEndpoint}/b2b/contracts/termination/${terminationId}/status`,
      body,
    );
  }

  // Trigger Email Reminder for Test's on DEV.
  public triggerEmailReminder(terminationId: number): Observable<void> {
    return this.http.post<void>(
      `${environment.apiEndpoint}/b2b/dev/contracts/termination/${terminationId}/trigger-reminder`,
      null,
    );
  }

  public getTerminationAlerts(
    request: ContractTerminationAlertListRequestModel,
  ): Observable<ContractTerminationAlertListResponseModel> {
    let params: HttpParams = new HttpParams();

    params = params.set('page', request.page ?? 0);
    params = params.set('page_size', request.page_size ?? 20);
    params = params.set('sort_by', request.sort_by ?? 'reminderDate');
    params = params.set('sort_direction', request.sort_direction ?? 'ASC');
    params = params.set('active', request.active ?? true);

    if (request.search) {
      params = params.set('search', request.search);
    }

    return this.http.get<ContractTerminationAlertListResponseModel>(
      `${environment.apiEndpoint}/b2b/contracts/termination/alerts`,
      { params },
    );
  }
}
